import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useRole, useRoleModal } from '../../contexts/RoleContext';
import { 
  X, 
  DollarSign, 
  AlertCircle, 
  CheckCircle, 
  Printer,
  User,
  Banknote,
} from 'lucide-react';
import sessionRecorder from '../../utils/sessionRecorder';
import {
  pruneStaleWorkerSessions,
  reconcileAbandonedWorkerSessions
} from '../../utils/workerSessionSanitizer';
import DeviationService from '../../services/deviationService';
import { getPaymentMethodName } from '../../services/paymentMethodService';
import AdsModalPortal from '../ads/AdsModalPortal';
import AdsButton from '../ads/AdsButton';
import { usePresenceTransition } from '../../hooks/usePresenceTransition';
import { getDraftData, isReadModelReady } from '../../services/cacheReadModel';
import useFormatCurrency from '../../hooks/useFormatCurrency';
import { buildPrintAdsCssVariables, buildPrintAdsInkForceCss } from '../../utils/printAdsPalette';
import {
  buildEmptyProviderInputs,
  getCashRegisterProviderRows,
  parseProviderInputs,
  CASH_PROVIDER_ID
} from './cashRegisterProviderAmounts';
import { MoneyAmount } from '../common/MoneyAmount';
import { translateWithVars } from '../../utils/i18n/translateWithVars';
import { formatSafeDateTime } from '../../utils/formatSafety';
import { resolvePrintExportCapabilities } from '../../utils/workerPermissions';

const NewCashRegisterModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  mode, // 'login', 'logout', or 'pause'
  previousAmount = null,
  workerName = 'Worker',
  workerId = '',
  sessionType = 'login'
}) => {
  const { currentLanguage, isRTL, t } = useLanguage();
  const te = (key) => t(`newCashRegisterModal.${key}`);
  const tv = (key, vars) => translateWithVars(t(`newCashRegisterModal.${key}`), vars);
  const displayLocale = isRTL ? 'ar-MR' : 'fr-MR';
  const { currencyType } = useCurrency();
  const formatCurrency = useFormatCurrency();
  const { businessId, currentRole } = useRole();
  const { pendingCashRegisterAction } = useRoleModal();
  // During login confirm, currentRole may still be null — use pending worker role (D03).
  const printRole = currentRole || pendingCashRegisterAction?.workerRole || null;
  const canPrintSessionReport = resolvePrintExportCapabilities(printRole).canPrint;
  
  // Saisie par fournisseur (espèces + chaque électronique) — vide = 0
  const [providerInputs, setProviderInputs] = useState(() => buildEmptyProviderInputs());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedData, setConfirmedData] = useState(null);
  
  // Deviation state
  const [hasDeviation, setHasDeviation] = useState(false);
  const [deviation, setDeviation] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [handoverDeviation, setHandoverDeviation] = useState(null);
  const [logoutCalculation, setLogoutCalculation] = useState(null);
  
  // UI state
  const [isArabicInput, setIsArabicInput] = useState(false);
  const isLogout = mode === 'logout';
  const isLogin = mode === 'login';

  const providerRows = useMemo(() => getCashRegisterProviderRows(), []);

  const logoutExpected = useMemo(() => {
    if (!isOpen || !isLogout || !workerId || !businessId) return null;
    try {
      const payload = sessionRecorder.getEcartSessionPayload(workerId, businessId);
      return {
        cash: Number(payload.expectedAmount ?? payload.expectedCash) || 0,
        electronicTotal: Number(payload.expectedElectronicAmount ?? payload.expectedElectronic) || 0,
        salesByProvider: payload.electronicSalesByProvider || {},
        payload
      };
    } catch {
      return null;
    }
  }, [isOpen, isLogout, workerId, businessId]);

  const getAmountsSnapshot = () => parseProviderInputs(providerInputs, currencyType);

  const getExpectedForProvider = (providerId) => {
    if (!logoutExpected) return null;
    if (providerId === CASH_PROVIDER_ID) return logoutExpected.cash;
    return Number(logoutExpected.salesByProvider[providerId]) || 0;
  };

  /**
   * Login: espèces + tous les fournisseurs électroniques (flot d'ouverture).
   * Logout: espèces + lignes électroniques avec attendu > 0.
   * Pause: no amount rows (confirmation only).
   */
  const visibleProviderRows = useMemo(() => {
    if (isLogin) return providerRows;
    if (!isLogout) return [];
    const filtered = providerRows.filter((row) => {
      const expected = getExpectedForProvider(row.id);
      return Number(expected) > 0.001;
    });
    if (filtered.length === 0) {
      return providerRows.filter((row) => row.id === CASH_PROVIDER_ID);
    }
    return filtered;
  }, [isLogin, isLogout, providerRows, logoutExpected]);

  const showElectronicExpectedTotal =
    (isLogout && Number(logoutExpected?.electronicTotal) > 0.001) ||
    (isLogin && providerRows.some((row) => row.id !== CASH_PROVIDER_ID));

  /** Live écart while counting — same screen as amount entry. */
  const liveLogout = useMemo(() => {
    if (!isOpen || !isLogout || !workerId || !businessId) return null;
    try {
      const { cashAmount, electronicAmount } = parseProviderInputs(providerInputs, currencyType);
      const payload = sessionRecorder.getEcartSessionPayload(
        workerId,
        businessId,
        cashAmount,
        electronicAmount
      );
      return {
        payload,
        deviation: DeviationService.deviationFromSessionDetail(payload),
      };
    } catch {
      return null;
    }
  }, [isOpen, isLogout, workerId, businessId, providerInputs, currencyType]);

  const liveHasDeviation = Boolean(
    liveLogout?.deviation?.hasDeviation && !liveLogout?.deviation?.withinTolerance
  );

  // Reset state when modal opens / mode changes (fresh open only).
  // isOpen+mode as deps: does NOT re-run after isConfirmed flips true on same open.
  useEffect(() => {
    if (!isOpen) return;
    setProviderInputs(buildEmptyProviderInputs());
    setError('');
    setIsConfirmed(false);
    setConfirmedData(null);
    setHasDeviation(false);
    setDeviation(null);
    setExplanation('');
    setHandoverDeviation(null);
    setLogoutCalculation(null);
    setIsArabicInput(false);
    setIsLoading(false);
  }, [isOpen, mode]);

  // Function to detect Arabic characters
  const containsArabic = (text) => {
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text);
  };

  // Function to mask numbers when Arabic is detected
  const maskNumbers = (text) => {
    if (!text) return '';
    // If Arabic is detected, replace all numbers with asterisks
    if (containsArabic(text)) {
      return text.replace(/\d/g, '*');
    }
    return text;
  };

  const handleProviderInputChange = (providerId) => (e) => {
    const value = e.target.value;
    setProviderInputs((prev) => ({ ...prev, [providerId]: value }));
    setIsArabicInput(containsArabic(value));
  };

  const renderProviderAmountTable = ({ showExpected = false, readOnly = false } = {}) => {
    const snapshot = getAmountsSnapshot();
    const electronicCounted = snapshot.electronicAmount;
    const rows = visibleProviderRows;
    const showTotal = showElectronicExpectedTotal;
    const showExpectedCol = showExpected;

    return (
      <div className="ncrm-provider-table-wrap">
        {isLogin ? (
          <p className="ncrm-empty-zero-hint ncrm-empty-zero-hint--compact">
            {te('loginIntro')} · {te('emptyAsZeroHint')} · {te('devise')}: {currencyType}
          </p>
        ) : isLogout ? (
          <p className="ncrm-empty-zero-hint ncrm-empty-zero-hint--compact">
            {te('logoutIntro')} · {te('emptyAsZeroHint')} · {te('devise')}: {currencyType}
          </p>
        ) : (
          <p className="ncrm-empty-zero-hint ncrm-empty-zero-hint--compact">
            {te('emptyAsZeroHint')} · {te('devise')}: {currencyType}
          </p>
        )}
        <table className={`ncrm-provider-table${showExpectedCol ? ' ncrm-provider-table--with-expected' : ''}`}>
          <colgroup>
            <col className="ncrm-provider-table__col-label" />
            {showExpectedCol && <col className="ncrm-provider-table__col-expected" />}
            <col className="ncrm-provider-table__col-counted" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="ncrm-provider-table__col--label">{te('providerColumn')}</th>
              {showExpectedCol && (
                <th scope="col" className="ncrm-provider-table__col--expected">{te('expectedColumn')}</th>
              )}
              <th scope="col" className="ncrm-provider-table__col--counted">
                {readOnly ? te('amount') : te('countedColumn')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const label =
                row.id === CASH_PROVIDER_ID
                  ? te('cash')
                  : getPaymentMethodName(row.id, currentLanguage);
              const raw = providerInputs[row.id] ?? '';
              const displayValue = readOnly
                ? String(raw)
                : isArabicInput && containsArabic(raw)
                  ? maskNumbers(raw)
                  : raw;
              const expected = showExpectedCol ? getExpectedForProvider(row.id) : null;

              return (
                <tr key={row.id} className={row.id === CASH_PROVIDER_ID ? 'ncrm-provider-table__row--cash' : ''}>
                  <th scope="row">
                    {row.id === CASH_PROVIDER_ID ? <Banknote size={14} aria-hidden /> : null}
                    <span>{label}</span>
                  </th>
                  {showExpectedCol && (
                    <td className="ncrm-provider-table__expected">
                      <div className="ncrm-provider-table__cell-inner">
                      {expected != null ? (
                        <>
                          <MoneyAmount amount={expected} />
                          {row.id !== CASH_PROVIDER_ID && expected > 0 && (
                            <span className="ncrm-provider-table__expected-meta"> ({te('salesAttenduHint')})</span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                      </div>
                    </td>
                  )}
                  <td className="ncrm-provider-table__input-cell">
                    <div className="ncrm-provider-table__cell-inner">
                    {readOnly ? (
                      <MoneyAmount amount={snapshot.amountsByProvider[row.id] || 0} />
                    ) : (
                      <input
                        type="text"
                        inputMode="decimal"
                        className={`ncrm-provider-input ${isArabicInput ? 'rtl' : ''}`}
                        value={displayValue}
                        onChange={handleProviderInputChange(row.id)}
                        placeholder="0"
                        autoFocus={index === 0}
                        aria-label={label}
                      />
                    )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {showTotal && (
              <tr className="ncrm-provider-table__row--total">
                <th scope="row">{te('totalRow')}</th>
                {showExpectedCol ? (
                  <td className="ncrm-provider-table__expected">
                    <div className="ncrm-provider-table__cell-inner">
                      <MoneyAmount amount={logoutExpected?.electronicTotal ?? 0} />
                    </div>
                  </td>
                ) : null}
                <td className="ncrm-provider-table__input-cell ncrm-provider-table__cell--strong">
                  <div className="ncrm-provider-table__cell-inner">
                  <MoneyAmount amount={electronicCounted} />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const handleFinalConfirm = async () => {
    setIsLoading(true);
    setError('');

    try {
      const amounts = getAmountsSnapshot();
      const { cashAmount: finalCashAmount, electronicAmount: finalElectronicAmount, totalAmount, amountsByProvider } = amounts;

      if (finalCashAmount < 0 || finalElectronicAmount < 0) {
        setError(te('veuillezSaisirMontantValide'));
        setIsLoading(false);
        return;
      }

      let activeDeviation = null;
      let activePayload = null;

      if (mode === 'logout') {
        activePayload =
          liveLogout?.payload ||
          sessionRecorder.getEcartSessionPayload(
            workerId,
            businessId,
            finalCashAmount,
            finalElectronicAmount
          );
        activeDeviation =
          liveLogout?.deviation ||
          DeviationService.deviationFromSessionDetail(activePayload);
        setLogoutCalculation(activePayload);

        const needsExplanation =
          Boolean(activeDeviation?.hasDeviation && !activeDeviation?.withinTolerance);

        if (needsExplanation && (!explanation || !explanation.trim())) {
          setHasDeviation(true);
          setDeviation(activeDeviation);
          setError(te('veuillezSaisirRaisonDifference'));
          setIsLoading(false);
          return;
        }

        setHasDeviation(needsExplanation);
        setDeviation(needsExplanation ? activeDeviation : null);
      }

      const amount = totalAmount;
      const confirmPayload = {
        action: 'confirm',
        amount,
        cashAmount: finalCashAmount,
        electronicAmount: finalElectronicAmount,
        totalAmount,
        amountsByProvider,
        paymentMethod: 'cash',
        electronicPaymentMethod: null,
        explanation: explanation || null
      };

      if (mode === 'login') {
        if (businessId && workerId) {
          sessionRecorder.reconcileCashRegisterLockState(businessId);
          reconcileAbandonedWorkerSessions(businessId, workerId);
          pruneStaleWorkerSessions(businessId);
        }

        if (sessionRecorder.isCashRegisterLocked(businessId, workerId)) {
          setError(te('caisseActuellementVerrouilleeVeuillezAttendre'));
          setIsLoading(false);
          return;
        }

        const pauseStatus = sessionRecorder.isCashRegisterPaused(businessId);
        if (pauseStatus) {
          setError(te('caissePauseVeuillezAttendreReprise'));
          setIsLoading(false);
          return;
        }

        if (handoverDeviation && handoverDeviation.hasDeviation) {
          sessionRecorder.updateDeviationExplanation(
            handoverDeviation.deviation.id,
            explanation,
            businessId
          );
        }

        await onConfirm(confirmPayload);
        // Login: parent closes modal and opens POS — no print step on open.
        return;
      } else if (mode === 'logout') {
        const drafts = isReadModelReady() ? getDraftData(businessId) : [];
        if (drafts && drafts.length > 0) {
          setError(tv('draftsLogoutBlocked', { count: drafts.length }));
          setIsLoading(false);
          return;
        }

        sessionRecorder.resumeCashRegister(businessId);
        await onConfirm(confirmPayload);
        // Logout: settle first, then success + optional print before Accueil.
        setConfirmedData(confirmPayload);
        setIsConfirmed(true);
        return;
      } else if (mode === 'pause') {
        await onConfirm({ action: 'pause' });
        return;
      }

      setConfirmedData(confirmPayload);
      setIsConfirmed(true);
    } catch (error) {
      console.error('Cash register error:', error);
      setError(te('erreurProduiteVeuillezReessayer'));
    } finally {
      setIsLoading(false);
    }
  };

  const printSessionReport = async () => {
    if (!canPrintSessionReport) return;
    try {
      const amounts = getAmountsSnapshot();
      const now = formatSafeDateTime(new Date(), displayLocale);
      const modeLabel =
        mode === 'login'
          ? te('modeLogin')
          : mode === 'logout'
            ? te('modeLogout')
            : te('modePause');

      const deviationRows =
        hasDeviation && deviation
          ? `
            <div class="row"><span>${te('deviation')}</span><span>${formatCurrency(Number(deviation.totalDifference ?? deviation.difference) || 0)}</span></div>
            <div class="row"><span>${te('attendu')}</span><span>${formatCurrency(Number(deviation.expectedAmount) || 0)}</span></div>
            <div class="row"><span>${te('reel')}</span><span>${formatCurrency(Number(deviation.actualAmount) || 0)}</span></div>
          `
          : '';

      const htmlLang = isRTL ? 'ar' : 'fr';
      const htmlDir = isRTL ? 'rtl' : 'ltr';
      const html = `<!DOCTYPE html>
<html lang="${htmlLang}" dir="${htmlDir}">
<head>
  <meta charset="UTF-8">
  <title>${te('recuCaisse')}</title>
  <style>
    ${buildPrintAdsCssVariables()}
    ${buildPrintAdsInkForceCss()}
    body { font-family: 'Courier New', monospace; margin: 0; padding: 8px; font-size: 11px; direction: ${htmlDir}; }
    .receipt { width: 58mm; margin: 0 auto; }
    .title { text-align: center; font-weight: bold; font-size: 13px; margin-bottom: 8px; }
    .row { display: flex; justify-content: space-between; margin: 4px 0; gap: 8px; }
    .sep { border-top: 1px dotted var(--print-text, #111827); margin: 8px 0; }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="title">${te('recuCaisse')}</div>
    <div class="row"><span>${te('colType')}</span><span>${modeLabel}</span></div>
    <div class="row"><span>${te('colDate')}</span><span>${now}</span></div>
    <div class="row"><span>${te('colWorker')}</span><span>${workerName}</span></div>
    <div class="sep"></div>
    <div class="row"><span>${te('cash')}</span><span>${formatCurrency(amounts.cashAmount)}</span></div>
    <div class="row"><span>${te('electronic')}</span><span>${formatCurrency(amounts.electronicAmount)}</span></div>
    <div class="row"><strong>${te('total')}</strong><strong>${formatCurrency(amounts.totalAmount)}</strong></div>
    ${deviationRows}
    ${explanation ? `<div class="sep"></div><div>${te('note')}: ${explanation}</div>` : ''}
  </div>
</body>
</html>`;

      const printFacade = (await import('../../services/printFacade')).default;
      const result = await printFacade.printWithPreviewOrSilent(html);
      if (!result?.success && typeof window !== 'undefined' && !window.electronAPI) {
        const w = window.open('', '_blank', 'width=400,height=600');
        if (w) {
          w.document.write(html);
          w.document.close();
          w.print();
          w.close();
        }
      }
    } catch (error) {
      console.error('Error printing session report:', error);
    }
  };

  const { present, closing } = usePresenceTransition(Boolean(isOpen));
  if (!present) return null;

  return (
    <AdsModalPortal>
      <div
        className={`new-cash-register-modal-overlay ncrm-overlay--fullscreen${
          closing ? ' ads-modal-exiting' : ' ads-modal-entering'
        }`}
        data-ads-modal-state={closing ? 'closing' : 'open'}
      >
      <div className={`new-cash-register-modal ncrm--fullscreen ncrm--onescreen ncrm-mode-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-icon">
            <DollarSign size={28} />
          </div>
          <div className="ncrm-header-text">
            <h2 className="modal-title">
              {mode === 'login' 
                ? te('saisirMontantCaisse')
                : mode === 'logout' 
                  ? te('confirmerMontantRestant')
                  : te('pauseCaisse')
              }
            </h2>
            {workerName ? (
              <div className="ncrm-worker-line">
                <User size={14} aria-hidden />
                <span>{te('workerLabel')}: <strong>{workerName}</strong></span>
              </div>
            ) : null}
          </div>
          <AdsButton
            type="button"
            variant="ghost"
            size="sm"
            className="close-button"
            onClick={onClose}
            aria-label={te('close')}
            disabled={isLoading}
          >
            <X size={20} aria-hidden />
          </AdsButton>
        </div>

        {/* One-screen body — no multi-step wizard */}
        <div className="ncrm-modal-body">
        {isConfirmed ? (
          <div className="step-content step-content--success">
            <div className="ncrm-success-inline">
              <CheckCircle size={36} aria-hidden />
              <h3>
                {isLogout
                  ? te('farewellCashierTitle')
                  : te('operationConfirmeeSucces')}
              </h3>
              <p>
                {isLogout
                  ? tv('farewellCashierMessage', { name: workerName || te('workerLabel') })
                  : te('operationEteConfirmeeSuccesVous')}
              </p>
              {isLogout ? (
                <p className="ncrm-logout-print-hint">{te('operationLogoutSuccesPrint')}</p>
              ) : null}
            </div>
            <div className="action-buttons action-buttons--sticky">
              {isLogout && canPrintSessionReport && (
                <AdsButton type="button" variant="primary" onClick={printSessionReport}>
                  <Printer size={16} />
                  {te('imprimerRapport')}
                </AdsButton>
              )}
              <AdsButton type="button" variant="secondary" onClick={onClose}>
                {isLogout ? te('closeAndExit') : te('close')}
              </AdsButton>
            </div>
          </div>
        ) : (
          <div className="step-content step-content--amount step-content--onescreen">
            <div className="ncrm-step-main">
              {isLogout && liveLogout?.deviation && (
                <div className="ncrm-kpi-strip" aria-label={te('resumeDifference')}>
                  <div className="ncrm-kpi">
                    <span className="ncrm-kpi__label">{te('attendu')}</span>
                    <span className="ncrm-kpi__value">
                      <MoneyAmount
                        amount={
                          Number(liveLogout.deviation.expectedTotalAmount ??
                            ((liveLogout.deviation.expectedAmount || 0) +
                              (liveLogout.deviation.expectedElectronicAmount || 0))) || 0
                        }
                      />
                    </span>
                  </div>
                  <div className="ncrm-kpi">
                    <span className="ncrm-kpi__label">{te('reel')}</span>
                    <span className="ncrm-kpi__value">
                      <MoneyAmount
                        amount={
                          Number(liveLogout.deviation.actualTotalAmount ??
                            ((liveLogout.deviation.actualAmount || 0) +
                              (liveLogout.deviation.actualElectronicAmount || 0))) || 0
                        }
                      />
                    </span>
                  </div>
                  <div
                    className={`ncrm-kpi ncrm-kpi--ecart${
                      liveHasDeviation
                        ? liveLogout.deviation.type === 'shortage'
                          ? ' ncrm-kpi--neg'
                          : ' ncrm-kpi--pos'
                        : ''
                    }`}
                  >
                    <span className="ncrm-kpi__label">
                      {te('difference')}
                      {liveHasDeviation
                        ? ` · ${liveLogout.deviation.type === 'shortage' ? te('manque') : te('surplus')}`
                        : ''}
                    </span>
                    <span className="ncrm-kpi__value">
                      <MoneyAmount
                        amount={
                          Number(
                            liveLogout.deviation.totalDifference ??
                              liveLogout.deviation.difference ??
                              0
                          ) || 0
                        }
                      />
                    </span>
                  </div>
                </div>
              )}

              {isLogin && workerName ? (
                <div className="ncrm-greeting ncrm-greeting--welcome" role="status">
                  <strong>{tv('welcomeCashierMessage', { name: workerName })}</strong>
                  <span>{te('welcomeCashierHint')}</span>
                </div>
              ) : null}

              {(isLogin || isLogout) && renderProviderAmountTable({ showExpected: isLogout })}

              {mode === 'pause' && (
                <div className="ncrm-greeting ncrm-greeting--pause" role="status">
                  <strong>{te('pauseWelcomeTitle')}</strong>
                  <span>
                    {tv('pauseWelcomeMessage', {
                      name: workerName || te('workerLabel')
                    })}
                  </span>
                </div>
              )}

              {isLogout && liveHasDeviation && (
                <div className="ncrm-onescreen-ecart">
                  <label className="input-label" htmlFor="ncrm-ecart-reason">
                    {te('raisonDifference')}
                  </label>
                  <input
                    id="ncrm-ecart-reason"
                    type="text"
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    placeholder={te('saisissezRaisonDifference')}
                    className="ncrm-ecart-input"
                    autoComplete="off"
                  />
                </div>
              )}

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </div>

            <div className="action-buttons action-buttons--sticky">
              <AdsButton
                type="button"
                variant="primary"
                onClick={handleFinalConfirm}
                disabled={isLoading}
              >
                {isLoading
                  ? te('processing')
                  : mode === 'pause'
                    ? te('pauseConfirmAction')
                    : te('confirmationFinale')}
              </AdsButton>
              <AdsButton type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                {te('cancel')}
              </AdsButton>
            </div>
          </div>
        )}
        </div>
      </div>
      </div>
    </AdsModalPortal>
  );
};

export default NewCashRegisterModal;




